import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { capacidades } from "@/lib/capacidades";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import SiteForm from "@/components/forms/SiteForm";
import { Search, Plus, Users, Cpu, ChevronRight } from "lucide-react";
import { TecnicoIcono } from "@/components/StatusBits";

// El agregado de readiness del sitio sólo viaja en la respuesta AUTORIZADA del listado. Para los
// roles institucionales el DTO externo (Gate D, SITIO_KEYS congelado) NO lo incluye: antes el
// `r || {listos:0, incompletos:0}` pintaba un CERO INVENTADO. Ahora la ausencia (o un agregado
// incompleto) se declara "—". Un cero REAL sigue siendo 0. No se consulta nada por sitio.
const numOrNull = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
export function resumenSitio(r) {
  if (!r || typeof r !== "object") return { conocido: false, listos: null, incompletos: null };
  const listos = numOrNull(r.listos);
  const incompletos = numOrNull(r.incompletos);
  if (listos === null || incompletos === null) return { conocido: false, listos, incompletos };
  return { conocido: true, listos, incompletos };
}

function ResumenReadiness({ r, testId }) {
  const res = resumenSitio(r);
  const dato = (v) => (v === null ? "—" : v);
  return (
    <span data-testid={testId} data-conocido={res.conocido ? "si" : "no"}
      title={res.conocido ? undefined : "No disponible con tu nivel de acceso desde este listado."}
      className="inline-flex items-center gap-2 text-xs">
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${res.listos === null ? "bg-slate-50 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>
        {dato(res.listos)} listos
      </span>
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${res.incompletos > 0 ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-500"}`}>
        {dato(res.incompletos)} incompletos
      </span>
    </span>
  );
}

export default function SitesList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const caps = capacidades(user);
  const [sitios, setSitios] = useState([]);
  const [q, setQ] = useState("");
  const [modalidad, setModalidad] = useState("todas");
  const [readiness, setReadiness] = useState("todos");
  const [orden, setOrden] = useState("nombre");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/sitios").then((r) => setSitios(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    let arr = sitios.filter((s) => {
      if (t) {
        const hay = [s.nombre, s.direccion, s.ciudad, ...(s.personas_nombres || [])].join(" ").toLowerCase();
        if (!hay.includes(t)) return false;
      }
      if (modalidad !== "todas" && (s.modalidad || "residencial") !== modalidad) return false;
      // Un readiness DESCONOCIDO no se clasifica como listo ni como incompleto: queda fuera de
      // los dos filtros de readiness en lugar de contarse como 0.
      const res = resumenSitio(s.readiness_resumen);
      if (readiness === "con_incompletos" && !(res.conocido && res.incompletos > 0)) return false;
      if (readiness === "todo_listo" && !(res.conocido && res.incompletos === 0)) return false;
      return true;
    });
    const inc = (s) => {
      const res = resumenSitio(s.readiness_resumen);
      return res.conocido ? res.incompletos : -1; // desconocido al final, nunca empatado con 0
    };
    arr = [...arr].sort((a, b) => {
      if (orden === "personas") return (b.num_personas || 0) - (a.num_personas || 0);
      if (orden === "incompletos") return inc(b) - inc(a);
      return (a.nombre || "").localeCompare(b.nombre || "");
    });
    return arr;
  }, [q, sitios, modalidad, readiness, orden]);

  return (
    <div className="tema-noche-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-[#012472]">Sitios</h1>
          <p className="text-sm text-slate-500 mt-1">{sitios.length} sitio(s) donde Galarya presta el servicio de monitoreo.</p>
        </div>
        {caps.crearSitio && (
          <Button data-testid="add-site-button" onClick={() => setFormOpen(true)} className="rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white">
            <Plus strokeWidth={1.75} className="h-4 w-4 mr-1.5" /> Nuevo sitio
          </Button>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative flex-1 max-w-md">
          <Search strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="site-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por sitio, persona o dirección…" className="pl-10 rounded-xl bg-white" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={modalidad} onValueChange={setModalidad}>
            <SelectTrigger data-testid="site-filter-modalidad" className="rounded-xl bg-white w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Toda modalidad</SelectItem>
              <SelectItem value="residencial">Residencial</SelectItem>
              <SelectItem value="institucional">Institucional</SelectItem>
            </SelectContent>
          </Select>
          <Select value={readiness} onValueChange={setReadiness}>
            <SelectTrigger data-testid="site-filter-readiness" className="rounded-xl bg-white w-[170px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todo readiness</SelectItem>
              <SelectItem value="con_incompletos">Con incompletos</SelectItem>
              <SelectItem value="todo_listo">Todo listo</SelectItem>
            </SelectContent>
          </Select>
          <Select value={orden} onValueChange={setOrden}>
            <SelectTrigger data-testid="site-sort" className="rounded-xl bg-white w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="nombre">Orden: nombre</SelectItem>
              <SelectItem value="personas">Orden: personas</SelectItem>
              <SelectItem value="incompletos">Orden: incompletos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <p className="text-slate-400">Cargando sitios…</p>
      ) : filtered.length === 0 ? (
        <p data-testid="sites-empty" className="text-slate-400">No se encontraron sitios.</p>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] divide-y divide-gray-100 overflow-hidden">
          {filtered.map((s) => (
            <button key={s.id} data-testid={`site-row-${s.id}`} onClick={() => navigate(`/sitios/${s.id}`)}
              className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-slate-50/70 transition-colors group">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 title={s.nombre || ""} className="text-base font-medium text-[#012472] max-w-full break-words [overflow-wrap:anywhere] line-clamp-2">{s.nombre || "(sin nombre)"}</h3>
                  <span className="text-[10px] uppercase tracking-wide rounded-full bg-[#012472]/5 text-[#012472] px-2 py-0.5">
                    {(s.modalidad || "residencial") === "institucional" ? "Institucional" : "Residencial"}
                  </span>
                  {s.estado === "Inactivo" && <span className="text-[10px] rounded-full bg-slate-100 text-slate-500 px-2 py-0.5">Inactivo</span>}
                </div>
                <p title={`${s.ciudad || ""}${s.direccion ? ` · ${s.direccion}` : ""}`} className="text-xs text-slate-400 mt-0.5 break-words [overflow-wrap:anywhere] line-clamp-2">{s.ciudad || "—"}{s.direccion ? ` · ${s.direccion}` : ""}</p>
              </div>
              {/* GC94: el número de residentes también en MÓVIL (antes sólo ≥sm). Dato ausente
                  (DTO sin el agregado) se declara «—», nunca cero. */}
              <span data-testid={`site-personas-${s.id}`} title="Residentes activos en el sitio"
                className="sm:hidden flex items-center gap-1 text-xs text-slate-500 shrink-0">
                <Users strokeWidth={1.5} className="h-4 w-4 text-slate-400" />
                {typeof s.num_personas === "number" ? s.num_personas : "—"}
              </span>
              <div className="hidden sm:flex items-center gap-4 text-xs text-slate-500 shrink-0">
                <span className="flex items-center gap-1"><Users strokeWidth={1.5} className="h-4 w-4 text-slate-400" /> {typeof s.num_personas === "number" ? s.num_personas : "—"}</span>
                <span className="flex items-center gap-1"><Cpu strokeWidth={1.5} className="h-4 w-4 text-slate-400" /> {typeof s.num_dispositivos === "number" ? s.num_dispositivos : "—"}</span>
                <span className="flex items-center gap-1">
                  <TecnicoIcono estado={s.tecnico_estado} ok={s.tecnico_ok} testId={`site-tecnico-${s.id}`} />
                </span>
              </div>
              <ResumenReadiness r={s.readiness_resumen} testId={`site-readiness-${s.id}`} />
              <ChevronRight strokeWidth={1.5} className="h-5 w-5 text-slate-300 group-hover:text-[#01CBE3] transition-colors shrink-0" />
            </button>
          ))}
        </div>
      )}

      <SiteForm open={formOpen} onOpenChange={setFormOpen} site={null} onSaved={load} />
    </div>
  );
}
